import React from "react";
import { Button, Paper, Stack, TextField, Typography } from "@mui/material";

import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { ClearCallLogoTitle } from "../../../components/clear-call-logo";
import { useUpdateMeetingAttendee } from "../../../store";
import { User } from "../../../types";

interface UpdateUserInfoPageProps {
  fullName: string;
  jobTitle: string;
  userId: string;
  meetingId: string;
  joinCode: string;
  onSuccess: (fullName: string, jobTitle: string) => void;
}

const UpdateUserInfoPage: React.FC<UpdateUserInfoPageProps> = ({
  fullName,
  jobTitle,
  userId,
  meetingId,
  joinCode,
  onSuccess,
}) => {
  const user = {
    fullName,
    jobTitle,
  };

  const updateSuccess = (data: User | null) => {
    toast.success("Set Name");
    onSuccess(data?.fullName ?? user.fullName, data?.jobTitle ?? user.jobTitle);
  };

  const updateError = (error: unknown) => {
    console.error("Failed to update", error);
    toast.error("Failed to update name");
  };

  const { mutate: updateUser } = useUpdateMeetingAttendee(
    updateSuccess,
    updateError
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: user.fullName,
      jobTitle: user.jobTitle,
    },
    validationSchema: Yup.object({
      fullName: Yup.string()
        .required("Full Name is required")
        .min(2, "Full Name is too short")
        .max(24, "Full Name is too long"),
      jobTitle: Yup.string()
        .required("Job Title is required")
        .max(24, "Job Title is too long"),
    }),
    onSubmit: (values) => {
      const { fullName, jobTitle } = values;

      if (fullName === user.fullName && jobTitle === user.jobTitle) {
        onSuccess(fullName, jobTitle);
        return;
      }

      updateUser({
        userId,
        meetingId,
        joinCode,
        user: { fullName, jobTitle },
      });
    },
  });

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(0deg,rgb(222, 238, 253),rgb(172, 220, 226))",
      }}
    >
      <Paper
        elevation={20}
        sx={{
          p: 2,
          pt: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 2,
        }}
      >
        <ClearCallLogoTitle size={120} />

        <Stack
          spacing={3}
          sx={{
            mt: 3,
            width: "100%",
            maxWidth: 620,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            p: 3,
            backgroundColor: "#FAFAFA",
            boxSizing: "border-box",
          }}
        >
          <Stack spacing={0.5} alignItems="center">
            <Typography variant="h4" component="h1" textAlign="center">
              Confirm Your Details
            </Typography>

            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
            >
              Please provide your name and job title before joining the
              meeting:
            </Typography>
          </Stack>

          <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
            <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
              <TextField
                fullWidth
                id="fullName"
                name="fullName"
                label="Full Name"
                variant="outlined"
                autoComplete="fullName"
                value={formik.values.fullName}
                onChange={formik.handleChange}
                error={formik.touched.fullName && Boolean(formik.errors.fullName)}
                helperText={formik.touched.fullName && formik.errors.fullName}
              />
              <TextField
                fullWidth
                id="jobTitle"
                name="jobTitle"
                label="Job Title"
                variant="outlined"
                autoComplete="jobTitle"
                value={formik.values.jobTitle}
                onChange={formik.handleChange}
                error={formik.touched.jobTitle && Boolean(formik.errors.jobTitle)}
                helperText={formik.touched.jobTitle && formik.errors.jobTitle}
              />
              <Button type="submit" variant="contained" fullWidth disabled={false}>
                Confirm Details
              </Button>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default UpdateUserInfoPage;
